/**
 * WEB-2936 — надписи РЕДКИХ блоков публичной воронки (хвост рендерера,
 * `components/editor/public-blocks/*`) по языку воронки.
 *
 * Почему не в ядре `publicQuizStrings.ts`. Ядро (en, ru) едет КАЖДОМУ
 * покупателю в первом экране, а запаса веса у первого экрана почти нет
 * (храповик `FIRST_SCREEN_GZIP_CEILING_BYTES`). Надписи редких блоков
 * обычной воронке не нужны ни байтом: этот файл импортируют только доли хвоста
 * рендерера, а они едут в ленивом куске `extended` по составу воронки. Тот же
 * приём, что у надписей ссылки предпросмотра (`previewLinkStrings.ts`,
 * WEB-2115), но все языки — здесь же, а не полем языкового пакета: поле пакета
 * стоило бы строки переноса в `packToStrings`, а она живёт в первом экране.
 *
 * Язык выбирает та же дверь, что у всех надписей плеера —
 * `resolvePublicQuizLocale` (незнакомый язык — английский). Полноту наборов
 * по всем языкам держит тип (`Record` по `SupportedPublicQuizLocale`: новый
 * язык плеера не соберётся без строки здесь) и сторож
 * `utils/publicExtendedBlockStrings.test.ts` (ключи как у английского, свои
 * значения, без кириллицы в английском).
 */
import {
  resolvePublicQuizLocale,
  type SupportedPublicQuizLocale,
} from './publicQuizStrings';

export interface ExtendedBlockStrings {
  /** Заглушка галереи без единой картинки (`gallery`). */
  galleryEmpty: string;
  /**
   * WEB-2942: подпись предела размера у загрузки фото (`photo_upload`);
   * шаблон с одной подстановкой `{size}` — число мегабайт.
   */
  photoUploadMaxSize: string;
}

const ZH: ExtendedBlockStrings = {
  galleryEmpty: '暂无图片',
  photoUploadMaxSize: '最大 {size} MB',
};

const STRINGS: Readonly<
  Record<SupportedPublicQuizLocale, ExtendedBlockStrings>
> = {
  en: { galleryEmpty: 'No images', photoUploadMaxSize: 'Max size: {size}MB' },
  ru: {
    galleryEmpty: 'Нет изображений',
    photoUploadMaxSize: 'Не больше {size} МБ',
  },
  es: {
    galleryEmpty: 'Sin imágenes',
    photoUploadMaxSize: 'Tamaño máximo: {size} MB',
  },
  de: {
    galleryEmpty: 'Keine Bilder',
    photoUploadMaxSize: 'Max. Größe: {size} MB',
  },
  fr: {
    galleryEmpty: 'Aucune image',
    photoUploadMaxSize: 'Taille max. : {size} Mo',
  },
  it: {
    galleryEmpty: 'Nessuna immagine',
    photoUploadMaxSize: 'Dimensione max: {size} MB',
  },
  pt: {
    galleryEmpty: 'Sem imagens',
    photoUploadMaxSize: 'Tamanho máx.: {size} MB',
  },
  'pt-BR': {
    galleryEmpty: 'Nenhuma imagem',
    photoUploadMaxSize: 'Tamanho máximo: {size} MB',
  },
  zh: ZH,
  'zh-CN': ZH,
  'zh-TW': { galleryEmpty: '暫無圖片', photoUploadMaxSize: '最大 {size} MB' },
  ja: {
    galleryEmpty: '画像がありません',
    photoUploadMaxSize: '最大 {size} MB',
  },
  ko: {
    galleryEmpty: '이미지가 없습니다',
    photoUploadMaxSize: '최대 {size}MB',
  },
  ar: {
    galleryEmpty: 'لا توجد صور',
    photoUploadMaxSize: 'الحد الأقصى للحجم: {size} ميغابايت',
  },
  he: {
    galleryEmpty: 'אין תמונות',
    photoUploadMaxSize: 'גודל מרבי: {size} MB',
  },
  hi: {
    galleryEmpty: 'कोई चित्र नहीं',
    photoUploadMaxSize: 'अधिकतम आकार: {size} MB',
  },
  tr: {
    galleryEmpty: 'Görsel yok',
    photoUploadMaxSize: 'Maks. boyut: {size} MB',
  },
  pl: {
    galleryEmpty: 'Brak obrazów',
    photoUploadMaxSize: 'Maks. rozmiar: {size} MB',
  },
  nl: {
    galleryEmpty: 'Geen afbeeldingen',
    photoUploadMaxSize: 'Max. grootte: {size} MB',
  },
  sv: {
    galleryEmpty: 'Inga bilder',
    photoUploadMaxSize: 'Max storlek: {size} MB',
  },
  da: {
    galleryEmpty: 'Ingen billeder',
    photoUploadMaxSize: 'Maks. størrelse: {size} MB',
  },
  no: {
    galleryEmpty: 'Ingen bilder',
    photoUploadMaxSize: 'Maks. størrelse: {size} MB',
  },
  fi: {
    galleryEmpty: 'Ei kuvia',
    photoUploadMaxSize: 'Enimmäiskoko: {size} Mt',
  },
  cs: {
    galleryEmpty: 'Žádné obrázky',
    photoUploadMaxSize: 'Max. velikost: {size} MB',
  },
  el: {
    galleryEmpty: 'Δεν υπάρχουν εικόνες',
    photoUploadMaxSize: 'Μέγ. μέγεθος: {size} MB',
  },
  uk: {
    galleryEmpty: 'Немає зображень',
    photoUploadMaxSize: 'Не більше {size} МБ',
  },
  ro: {
    galleryEmpty: 'Nicio imagine',
    photoUploadMaxSize: 'Dimensiune max.: {size} MB',
  },
  hu: {
    galleryEmpty: 'Nincsenek képek',
    photoUploadMaxSize: 'Max. méret: {size} MB',
  },
  th: {
    galleryEmpty: 'ไม่มีรูปภาพ',
    photoUploadMaxSize: 'ขนาดสูงสุด: {size} MB',
  },
  vi: {
    galleryEmpty: 'Không có hình ảnh',
    photoUploadMaxSize: 'Kích thước tối đa: {size} MB',
  },
  id: {
    galleryEmpty: 'Tidak ada gambar',
    photoUploadMaxSize: 'Ukuran maks.: {size} MB',
  },
};

/** Надписи редких блоков на языке воронки; незнакомый язык — английский. */
export function getExtendedBlockStrings(
  language?: string | null,
): ExtendedBlockStrings {
  return STRINGS[resolvePublicQuizLocale(language)];
}

/** «Не больше {size} МБ» → «Не больше 5 МБ» на языке воронки. */
export function photoUploadMaxSizeText(
  language: string | null | undefined,
  sizeMb: number,
): string {
  return getExtendedBlockStrings(language).photoUploadMaxSize.replace(
    '{size}',
    String(sizeMb),
  );
}
